import { useRef } from 'react'
import { Wifi, WifiOff, Loader2, RefreshCw } from 'lucide-react'

/**
 * Shared collab connection indicator for the editor toolbars.
 *
 * One place to render the connection state so every Yjs-backed editor stays
 * consistent and new editors don't re-implement the pill. States:
 *   - localMode                → "Local" (no live room)
 *   - reloading                → amber "Reconnecting…" (a forced Y.Doc rebuild
 *     is awaiting its first resync; the editor keeps the last content painted)
 *   - connected                → green "Connected"
 *   - was connected, now not   → amber "Reconnecting…" (the live socket dropped
 *     and the hook is retrying — covers the "wifi went red" handshake window)
 *   - gaveUp                    → red "Disconnected — reload" (the bounded
 *     reconnect exhausted its attempts; it is NOT retrying anymore, so the
 *     amber spinner would be a lie — an honest terminal state tells the user
 *     to reload instead of waiting forever)
 *   - never connected, not reloading → red "Disconnected"
 *
 * The last states are distinguished by `everConnectedRef` (without it a viewer
 * that legitimately has no live connection would show a perpetual "Reconnecting…"
 * spinner) and by `gaveUp` (without it a socket that surrendered after the
 * attempt cap would ALSO spin "Reconnecting…" forever while nothing reconnects).
 */
export function CollabStatusBadge({
  connected,
  reloading = false,
  localMode = false,
  gaveUp = false,
}: {
  connected: boolean
  reloading?: boolean
  localMode?: boolean
  gaveUp?: boolean
}) {
  const everConnectedRef = useRef(false)
  if (connected) everConnectedRef.current = true

  // Give-up is terminal: suppress the "Reconnecting…" spinner so the badge
  // doesn't claim to be retrying when the bounded controller has stopped.
  const reconnecting = !gaveUp && (reloading || (!connected && everConnectedRef.current))

  // Give-up is terminal — the reconnect controller has stopped. Offer a
  // user-driven refresh (reload = fresh provider = fresh controller) rather than
  // pretending to reconnect or silently re-arming.
  const isGiveUp = !localMode && gaveUp && !connected

  // Icon-only: the color carries the state, the wrapper's data-tooltip carries the words
  const label = localMode
    ? 'Local — no live connection'
    : isGiveUp
      ? 'Disconnected — click to reconnect (reloads the page)'
      : reconnecting
        ? (reloading ? 'Reconnecting — showing the last loaded version until it refreshes' : 'Reconnecting…')
        : connected ? 'Connected' : 'Disconnected'

  if (isGiveUp) {
    return (
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="flex items-center gap-1 text-red-500 hover:text-red-400 cursor-pointer"
        data-tooltip={label}
        aria-label={label}
      >
        <WifiOff size={14} />
        <RefreshCw size={12} />
      </button>
    )
  }

  return (
    <div className="flex items-center themed-text-muted" data-tooltip={label} aria-label={label}>
      {localMode ? (
        <Wifi size={14} className="themed-text-muted" />
      ) : reconnecting ? (
        <Loader2 size={14} className="animate-spin text-amber-500" />
      ) : connected ? (
        <Wifi size={14} className="text-green-500" />
      ) : (
        <WifiOff size={14} className="text-red-500" />
      )}
    </div>
  )
}
